import type { FormInputValues } from '@src/domains/orders';

/**
 * Practical ceiling of one cookie: browsers stop at ~4096 bytes, and an oversized
 * cookie is dropped without a word. The margin covers the name and the attributes.
 */
export const MAX_DRAFT_COOKIE_BYTES = 3800;

/** Chunk ceiling. A realistic order needs two or three; the cap keeps reads bounded. */
export const MAX_DRAFT_CHUNKS = 20;

/** Scoping by order keeps one order's draft from leaking into another. */
export const formInputsDraftKey = (orderId: string, chunk: number): string => `formInputs:${orderId}:${chunk}`;

/**
 * Stores only `{ hash: value }`. `inputType` is left out on purpose: it is derivable
 * from the `SaleFormInput` on hydration and is what weighs most in the payload.
 *
 * A cart of eight tariffs already passes the ceiling of a single cookie, so the
 * payload is chunked; every chunk carries the same expiry as the order.
 */
export const chunkFormInputsDraft = (values: FormInputValues): string[] | null => {
  const filled = Object.entries(values).filter(([, value]) => value !== '');
  const serialized = JSON.stringify(Object.fromEntries(filled));

  const chunks: string[] = [];
  let rest = serialized;

  while (rest.length > 0) {
    // Chunks are measured encoded, which is how the browser stores them: an accent
    // becomes six characters, so cutting by the raw length would overflow. The first
    // guess comes from the payload's own expansion ratio, so a large draft does not
    // re-encode the same prefix a dozen times on the way down.
    const encodedLength = encodeURIComponent(rest).length;
    const ratio = encodedLength / rest.length;
    let size = Math.min(rest.length, Math.max(1, Math.floor(MAX_DRAFT_COOKIE_BYTES / ratio)));

    while (size > 1 && encodeURIComponent(rest.slice(0, size)).length > MAX_DRAFT_COOKIE_BYTES) {
      size = Math.floor(size * 0.9);
    }

    chunks.push(rest.slice(0, size));
    rest = rest.slice(size);

    if (chunks.length > MAX_DRAFT_CHUNKS) {
      return null;
    }
  }

  return chunks;
};

/** Joins the chunks in order, stopping at the first one missing. */
export const parseFormInputsDraft = (chunks: Array<string | undefined>): FormInputValues => {
  const present: string[] = [];

  for (const chunk of chunks) {
    if (chunk === undefined) {
      break;
    }

    present.push(chunk);
  }

  if (present.length === 0) {
    return {};
  }

  try {
    const parsed: unknown = JSON.parse(present.join(''));

    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        (entry): entry is [string, string] => typeof entry[1] === 'string',
      ),
    );
  } catch {
    return {};
  }
};
